// Checkbox.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './Checkbox.css';

// Icons
import Icon from 'svg/Icon';

const CHECKED_BOX_PATH = 'M200-160q-33 0-56.5-23.5T120-240v-480q0-33 23.5-56.5T200-800h480q33 0 56.5 23.5T760-720v480q0 33-23.5 56.5T680-160H200Zm212-143 226-226-57-56-169 169-85-85-56 57 141 141Z';
const UNCHECKED_BOX_PATH = 'M200-160q-33 0-56.5-23.5T120-240v-480q0-33 23.5-56.5T200-800h480q33 0 56.5 23.5T760-720v480q0 33-23.5 56.5T680-160H200Zm0-80h480v-480H200v480Z';

/**
 * Checkbox Component
 *
 * This component renders a checkbox input field.
 *
 * @param {string} label The checkbox label.
 * @param {boolean} value The checkbox value.
 * @param {number} size The size of the checkbox icon.
 * @param {boolean} [strikethrough=false] When true, strikes through the label while checked.
 * @param {function} onChange The checkbox change handler.
 *
 * @returns {ReactNode} The rendered component.
 */
const Checkbox = ({
  label = '',
  value,
  size = 24,
  fontSize = '14px',
  fontWeight = 400,
  color,
  checkedColor,
  strikethrough = false,
  onChange,
}) => {
  const { theme } = useTheme();
  const isChecked = Boolean(value);
  const showStrikethrough = strikethrough && isChecked;

  return (
    <div className={[
      'checkbox-container',
      showStrikethrough ? 'is-strikethrough' : '',
    ].filter(Boolean).join(' ')}
    >
      <div
        className="checkbox-input"
        onClick={onChange}
      >
        {isChecked ? (
          <Icon
            iconKey="check_box"
            path={CHECKED_BOX_PATH}
            color={checkedColor || theme.foreground}
            width={size}
            height={size}
          />
        ) : (
          <Icon
            iconKey="check_box_outline_blank"
            path={UNCHECKED_BOX_PATH}
            color={color || theme.foregroundMuted}
            width={size}
            height={size}
          />
        )}
      </div>

      {label !== '' && (
        <div
          className="checkbox-label"
          style={{
            color: showStrikethrough ? theme.foregroundMuted : theme.foreground,
            fontSize,
            fontWeight,
            textDecoration: showStrikethrough ? 'line-through' : 'none',
          }}
          onClick={onChange}
        >
          {label}
        </div>
      )}
    </div>
  );
};

// Dev Tools
Checkbox.displayName = 'Checkbox';

export default Checkbox;
